// value_chips.ts - clickable affordance on enum-valued YAML scalars.
//
// A value whose slot the completion schema bounds (an enum or a boolean) is
// effectively a menu the editor already knows how to open - the chip makes
// that visible: a subtle underline always, a bordered chip with a chevron on
// hover, and a click that selects the whole value and opens the completion
// menu over it (the completion source serves the full domain for a selected
// value - see the selection branch in completion.ts).
//
// Detection reuses the completion core verbatim - `completionContextAt` +
// `buildCompletionList` against the same index the source queries - so a chip
// appears exactly where the menu would have options, and nothing needs a
// second schema walk to stay in sync.

import { startCompletion } from "@codemirror/autocomplete";
import { EditorSelection, Prec, RangeSetBuilder, type Extension } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";
import {
  buildCompletionList,
  completionContextAt,
  scalarPairOnLine,
  type CompletionIndex,
} from "./completion.js";

/**
 * The chip span for `lineNumber` (0-based), as character offsets within the
 * line, or null when the line holds no scalar pair or its slot is unbounded.
 * The span covers the authored value as written - quotes included - because
 * that is what a pick replaces.
 */
export function boundedValueSpan(
  index: CompletionIndex,
  source: string,
  lineNumber: number,
): { from: number; to: number } | null {
  const line = source.split("\n")[lineNumber] ?? "";
  const pair = scalarPairOnLine(line);
  if (pair === null) {
    return null;
  }
  const to = pair.valueStart + pair.value.length;
  const core = completionContextAt(source, { line: lineNumber, ch: to });
  if (core.mode !== "value") {
    return null;
  }
  const { list } = buildCompletionList(index, source, core, "");
  if (!list.some((item) => item.mode === "value")) {
    return null;
  }
  return { from: pair.valueStart, to };
}

const chipMark = Decoration.mark({ class: "cm-dct-enum-value" });

// Box-shadow rather than a border so the hover state never shifts layout;
// the chevron is generated content on hover only, and YAML values end their
// line in practice, so the one-glyph growth displaces nothing.
const chipTheme = EditorView.baseTheme({
  ".cm-dct-enum-value": {
    cursor: "pointer",
    borderBottom: "1px dotted rgba(100, 116, 139, 0.55)",
  },
  ".cm-dct-enum-value:hover": {
    boxShadow: "0 0 0 1px rgba(100, 116, 139, 0.55)",
    borderRadius: "3px",
    backgroundColor: "rgba(100, 116, 139, 0.08)",
  },
  ".cm-dct-enum-value:hover::after": {
    content: '"▾"',
    fontSize: "70%",
    opacity: "0.65",
    paddingLeft: "3px",
  },
  "&dark .cm-dct-enum-value": {
    borderBottomColor: "rgba(148, 163, 184, 0.55)",
  },
  "&dark .cm-dct-enum-value:hover": {
    boxShadow: "0 0 0 1px rgba(148, 163, 184, 0.55)",
    backgroundColor: "rgba(148, 163, 184, 0.12)",
  },
});

/**
 * The chip extension bundle for one completion index. Mounted by
 * `dbtChartsCompletionExtensions` beside the source that shares the index.
 */
export function dbtChartsValueChips(index: CompletionIndex): Extension[] {
  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.build(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.build(update.view);
        }
      }

      build(view: EditorView): DecorationSet {
        const source = view.state.doc.toString();
        const builder = new RangeSetBuilder<Decoration>();
        for (const range of view.visibleRanges) {
          let pos = range.from;
          while (pos <= range.to) {
            const line = view.state.doc.lineAt(pos);
            const span = boundedValueSpan(index, source, line.number - 1);
            if (span !== null) {
              builder.add(line.from + span.from, line.from + span.to, chipMark);
            }
            pos = line.to + 1;
          }
        }
        return builder.finish();
      }
    },
    {
      decorations: (instance) => instance.decorations,
      eventHandlers: {
        click(event: MouseEvent, view: EditorView) {
          if (event.button !== 0 || event.detail !== 1 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
            return false;
          }
          // Element-based, not positional: a click in the blank area past the
          // line end resolves positionally to the line's last offset — which
          // is the chip's own `to` on nearly every chip — and a caret-placing
          // click was being hijacked into a selection with a menu over it.
          // Only a click on the chip's painted span counts.
          const chip = (event.target as Element | null)?.closest(".cm-dct-enum-value");
          if (!chip) {
            return false;
          }
          const pos = view.posAtDOM(chip, 0);
          const hits: { from: number; to: number }[] = [];
          this.decorations.between(pos, pos + 1, (from, to) => {
            hits.push({ from, to });
            return false;
          });
          const hit = hits.at(0);
          if (hit === undefined) {
            return false;
          }
          view.dispatch({ selection: EditorSelection.range(hit.from, hit.to) });
          startCompletion(view);
          return true;
        },
      },
    },
  );
  return [Prec.low(plugin), chipTheme];
}
